import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import styles from '../integration-builder.module.css'
import { Accordion } from '../../../components/integration-builder/accordion'
import cls from 'classnames'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import { SnippetSchemaEnvironmentSampleFile } from '../../../components/integration-builder/code-selector/code-snippets/interfaces'
import { Button, Dialog, Tooltip } from '@paypalcorp/pp-react'
import { PayComplete } from '../../../components/icons/PayComplete/PayComplete'

const Preview: FunctionComponent = () => {
  const iframeRef = useRef(null)
  const isForcedHorizontal = false
  const expandPreview = true
  const [transactionDialog, setTransactionDialog] = useState({
    isOpen: false,
    title: '',
    subtitle: '',
    step: 0,
  })

  const {
    snippets = {},
    parameters,
    integrationType,
    options,
    setParameter,
  } = useContext(CheckoutSimulatorContext)

  const [clientId, setClientId] = useState(
    'ARo8VkLiF5aVnlHgZ1SwTPt7Pq218nO7pjJ68qLZ6jcl5JLQEenqJ8nAixxfh6btGoC8v8zASZ2awDwb',
  )
  const [appSecret, setAppSecret] = useState(
    'EHVT_O8TIvB1SM0iY0acwtHuYIz2AtDxkD8-ATnfH4F-jsHOwsb3c7_EvDWh7tjhtYxGZce4TUNMwBIw',
  )

  const baseURL = 'https://api-m.sandbox.paypal.com'

  async function generateClientToken() {
    const accessToken = await generateAccessToken()
    const response = await fetch(`${baseURL}/v1/identity/generate-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Accept-Language': 'en_US',
        'Content-Type': 'application/json',
      },
    })
    const data = await response.json()
    setParameter('client_token', data.client_token)
  }

  // access token is used to authenticate all REST API requests
  async function generateAccessToken() {
    const auth = Buffer.from(clientId + ':' + appSecret).toString('base64')
    const response = await fetch(`${baseURL}/v1/oauth2/token`, {
      method: 'POST',
      body: 'grant_type=client_credentials',
      headers: {
        Authorization: `Basic ${auth}`,
      },
    })
    const data = await response.json()
    return data.access_token
  }

  useEffect(() => {
    generateClientToken()
  }, [])

  const [frontendFiles, setFrontendFiles] =
    useState<SnippetSchemaEnvironmentSampleFile[]>()

  const standardSuccess = () => {
    setTransactionDialog({
      isOpen: true,
      title: 'Transaction completed',
      subtitle: 'Transaction ID - 25A49506MV7427301',
      step: 0,
    })
  }

  const standardError = () => {
    setTransactionDialog({
      isOpen: true,
      title: 'Payment could not be captured',
      subtitle: 'Error - invalid card number Debug ID - 9b2f285198d36',
      step: 0,
    })
  }

  const tdsSuccess = () => {
    setTransactionDialog({
      isOpen: true,
      title: 'Additional verification',
      subtitle: 'Your bank has requested additional verification',
      step: 1,
    })
  }

  useEffect(() => {
    setFrontendFiles(snippets?.frontend)
  }, [snippets])
  useEffect(() => {
    if (transactionDialog.isOpen === false && transactionDialog.step > 0) {
      setTransactionDialog({
        isOpen: true,
        title: 'Transaction completed',
        subtitle: 'Transaction ID - ABCDEFG123456',
        step: 0,
      })
    }
  }, [transactionDialog])

  useEffect(() => {
    window.addEventListener(
      'message',
      (
        ev: MessageEvent<{
          action: string
          integrationType: string
          tds: string
        }>,
      ) => {
        if (typeof ev.data !== 'object') return
        if (!ev.data.action) return

        if (ev.data.action === 'done') {
          if (ev.data.integrationType === 'standard') standardSuccess()
          else if (ev.data.tds === 'true') tdsSuccess()
          else standardSuccess()
        } else if (ev.data.action === 'error') {
          standardError()
        }
      },
    )
  }, [])

  const previewTitle = integrationType === 'standard' ? 'PayPal' : 'Expanded'

  const handleTransactionOkay = () => {
    if (transactionDialog.step > 0) {
      standardSuccess()
    } else {
      setTransactionDialog({ ...transactionDialog, isOpen: false })
    }
  }

  return (
    <div className={styles.checkout_simulator__preview}>
      <Dialog
        title=""
        hideTitle
        isOpen={transactionDialog.isOpen}
        closeOnBackgroundClick
        onClose={() => handleTransactionOkay()}
        center
      >
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '100px',
              marginBottom: '100px',
            }}
          >
            <PayComplete width="120" height="120" />
            <span
              style={{
                marginTop: '32px',
                fontFamily: 'PayPal Open',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '44px',
                lineHeight: '48px',
              }}
            >
              {transactionDialog.title}
            </span>
            <span
              style={{
                fontFamily: 'PayPal Open',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '18px',
                lineHeight: '24px',
                marginTop: '32px',
              }}
            >
              {transactionDialog.subtitle}
            </span>
            <Button
              secondary
              onClick={handleTransactionOkay}
              style={{ marginTop: '60px' }}
            >
              Okay
            </Button>
          </div>
        </div>
      </Dialog>
      <Accordion
        color="primary"
        heading={
          <h1 className={styles.checkout_simulator__preview_title}>
            <Tooltip
              name="tooltip1"
              description={
                integrationType === 'standard'
                  ? 'PayPal payments are presented with PayPal-branded buttons and payment is processed through the PayPal back-end.'
                  : 'Requires a merchant account and approval for hosted checkout functionality.'
              }
              windowId="window1"
              imagePosition="bottom-left"
            />
            {`${previewTitle} Checkout Preview`}
          </h1>
        }
        content={
          <>
            {expandPreview && !isForcedHorizontal && (
              <>
                <div
                  className={cls({
                    [styles.integration_builder__horizontal_btns_wrapper]:
                      isForcedHorizontal,
                  })}
                >
                  {frontendFiles &&
                    frontendFiles.length > 0 &&
                    frontendFiles
                      .filter((snippet) => snippet.injectionCode)
                      .map((snippet) => {
                        return (
                          <iframe
                            ref={iframeRef}
                            key={snippet.name}
                            className={
                              styles.integration_builder__iframe_vertical
                            }
                            style={{
                              height:
                                integrationType === 'standard'
                                  ? '350px'
                                  : '700px',
                            }}
                            name="paypaloverlay"
                            srcDoc={snippet.injectionCode!({
                              ...parameters,
                              options,
                            })}
                          />
                        )
                      })}
                </div>
              </>
            )}
          </>
        }
      />
    </div>
  )
}

export default Preview
